import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";

const perfilSchema = z.object({
  nombre: z.string().trim().min(1, "El nombre del perfil es obligatorio."),
});

type PerfilValues = z.infer<typeof perfilSchema>;

interface PerfilFormularioProps {
  nombreInicial?: string;
  /** Texto del botón que envía: "Crear perfil" / "Guardar cambios". */
  etiquetaEnvio: string;
  enviando: boolean;
  /** `errorDeNombre` deja pegar en el campo el mensaje que devuelve el 422. */
  onEnviar: (nombre: string, acciones: { errorDeNombre: (mensaje: string) => void }) => void;
  onCancelar?: () => void;
}

/**
 * Formulario del maestro de perfiles (pantallas 11 SA y 12 SA): un solo campo.
 * Alta y edición comparten campo, validación y tratamiento del 422, así que
 * comparten componente; lo que cambia es el rótulo del botón y el submit.
 */
export function PerfilFormulario({
  nombreInicial = "",
  etiquetaEnvio,
  enviando,
  onEnviar,
  onCancelar,
}: PerfilFormularioProps) {
  const form = useForm<PerfilValues>({
    resolver: zodResolver(perfilSchema),
    defaultValues: { nombre: nombreInicial },
  });

  function handleSubmit(valores: PerfilValues) {
    onEnviar(valores.nombre, {
      errorDeNombre: (mensaje) =>
        form.setError("nombre", { type: "server", message: mensaje }),
    });
  }

  return (
    <div className="bg-card w-full max-w-[30rem] rounded-xl border p-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
          <FormField
            control={form.control}
            name="nombre"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nombre del perfil</FormLabel>
                <FormControl>
                  <Input placeholder="Ej: Usuario Revisor" autoComplete="off" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={enviando} className="gap-2">
              {enviando && <Spinner className="text-primary-foreground" />}
              {etiquetaEnvio}
            </Button>
            {onCancelar && (
              <Button type="button" variant="outline" onClick={onCancelar}>
                Cancelar
              </Button>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
}
